import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { configurationAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { 
  BuildingOfficeIcon,
  PhotoIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  SwatchIcon,
  PencilIcon,
  CalendarDaysIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner, Button, Card, Input, Modal, ProtectedRoute, ConfirmModal } from '../components/ui';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import SubscriptionPlanCard from '../components/SubscriptionPlanCard';
import PlanFeatureUpgradePrompt from '../components/PlanFeatureUpgradePrompt';
import { usePlanFeature } from '../hooks/usePlanFeature';
import InvoicePrintSettingsForm from '../components/invoice-print/InvoicePrintSettingsForm';
import UiThemeSettingsForm from '../components/configuration/UiThemeSettingsForm';
import {
  InvoicePrintSettings,
  DEFAULT_INVOICE_PRINT_SETTINGS,
  normalizeInvoicePrintSettings,
  saveInvoicePrintSettingsToLocalStorage,
} from '../lib/invoicePrintSettings';
import {
  UiTheme,
  DEFAULT_UI_THEME,
  normalizeUiTheme,
  applyUiThemeToDocument,
  writeCachedUiTheme,
} from '../lib/uiTheme';
import { useAuth } from '../hooks/useAuth';

interface Configuration {
  id: number;
  companyName: string;
  address: string;
  phone: string;
  email: string;
  ruc: string;
  website?: string;
  logo?: string;
  currencyCode: string;
  locale: string;
  invoicePrintSettings?: InvoicePrintSettings | null;
  uiTheme?: UiTheme | null;
  billingTimezone?: string;
  monthlyInvoiceAutoEnabled?: boolean;
  monthlyInvoiceGenerationDay?: number;
  monthlyInvoiceGenerationHour?: number;
  createdAt: string;
  updatedAt: string;
}

const CURRENCY_OPTIONS: { value: string; label: string }[] = [
  { value: 'DOP', label: 'DOP — Peso dominicano' },
  { value: 'USD', label: 'USD — Dólar estadounidense' },
  { value: 'VES', label: 'VES — Bolívar soberano' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'COP', label: 'COP — Peso colombiano' },
  { value: 'MXN', label: 'MXN — Peso mexicano' },
  { value: 'PEN', label: 'PEN — Sol peruano' },
  { value: 'ARS', label: 'ARS — Peso argentino' },
  { value: 'BOB', label: 'BOB — Boliviano' },
  { value: 'CLP', label: 'CLP — Peso chileno' }
];

const LOCALE_OPTIONS: { value: string; label: string }[] = [
  { value: 'es-DO', label: 'Español (República Dominicana)' },
  { value: 'es-VE', label: 'Español (Venezuela)' },
  { value: 'es-CO', label: 'Español (Colombia)' },
  { value: 'es-MX', label: 'Español (México)' },
  { value: 'es-ES', label: 'Español (España)' },
  { value: 'es-AR', label: 'Español (Argentina)' },
  { value: 'es-CL', label: 'Español (Chile)' },
  { value: 'es-PE', label: 'Español (Perú)' },
  { value: 'en-US', label: 'English (US)' }
];

const BILLING_HOURS = Array.from({ length: 24 }, (_, i) => i);
const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

type ConfigurationFormData = {
  companyName: string;
  address: string;
  phone: string;
  email: string;
  ruc: string;
  website: string;
  currencyCode: string;
  locale: string;
  invoicePrintSettings: InvoicePrintSettings;
  uiTheme: UiTheme;
  billingTimezone: string;
  monthlyInvoiceAutoEnabled: boolean;
  monthlyInvoiceGenerationDay: number;
  monthlyInvoiceGenerationHour: number;
};

function extractConfiguration(response: unknown): Configuration | null {
  const body = (response as { data?: { data?: Configuration } })?.data;
  if (!body?.data || typeof body.data !== 'object') return null;
  return body.data;
}

function configurationToFormData(config: Configuration): ConfigurationFormData {
  return {
    companyName: config.companyName ?? '',
    address: config.address ?? '',
    phone: config.phone ?? '',
    email: config.email ?? '',
    ruc: config.ruc ?? '',
    website: config.website ?? '',
    currencyCode: config.currencyCode || 'DOP',
    locale: config.locale || 'es-DO',
    invoicePrintSettings: normalizeInvoicePrintSettings(config.invoicePrintSettings),
    uiTheme: normalizeUiTheme(config.uiTheme),
    billingTimezone: config.billingTimezone || 'America/Caracas',
    monthlyInvoiceAutoEnabled: config.monthlyInvoiceAutoEnabled ?? true,
    monthlyInvoiceGenerationDay: config.monthlyInvoiceGenerationDay ?? 1,
    monthlyInvoiceGenerationHour: config.monthlyInvoiceGenerationHour ?? 9,
  };
}

type ConfigSection = 'company' | 'regional' | 'billing' | 'appearance' | 'invoice';

const SectionEditActions: React.FC<{
  isEditing: boolean;
  saving?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}> = ({ isEditing, saving, onEdit, onCancel, onSave }) => {
  if (!isEditing) {
    return (
      <Button type="button" onClick={onEdit} variant="outline" size="sm" className="shrink-0">
        <PencilIcon className="h-4 w-4 mr-1.5" />
        Editar
      </Button>
    );
  }
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button type="button" onClick={onCancel} variant="outline" size="sm" disabled={saving}>
        <XMarkIcon className="h-4 w-4 mr-1.5" />
        Cancelar
      </Button>
      <Button type="button" onClick={onSave} variant="primary" size="sm" loading={saving}>
        <CheckIcon className="h-4 w-4 mr-1.5" />
        Guardar
      </Button>
    </div>
  );
};

const Configuration: React.FC = () => {
  const [editingSection, setEditingSection] = useState<ConfigSection | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'success' | 'info';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {}
  });
  const queryClient = useQueryClient();
  const { hasPermission, isAdmin } = usePermissions();
  const { user } = useAuth();
  const tenantId = user?.tenantId || user?.tenant?.id || null;
  const { enabled: hasEmailAutomation, isLoading: loadingEmailFeature } = usePlanFeature('emailAutomation');

  const [formData, setFormData] = useState<ConfigurationFormData>({
    companyName: '',
    address: '',
    phone: '',
    email: '',
    ruc: '',
    website: '',
    currencyCode: 'DOP',
    locale: 'es-DO',
    invoicePrintSettings: { ...DEFAULT_INVOICE_PRINT_SETTINGS },
    uiTheme: { ...DEFAULT_UI_THEME },
    billingTimezone: 'America/Caracas',
    monthlyInvoiceAutoEnabled: true,
    monthlyInvoiceGenerationDay: 1,
    monthlyInvoiceGenerationHour: 9,
  });

  const { data: configuration, isLoading } = useQuery(
    'configuration',
    configurationAPI.get,
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false
    }
  );

  const savedConfig = useMemo(() => extractConfiguration(configuration), [configuration]);

  useEffect(() => {
    if (!savedConfig || editingSection) return;
    setFormData(configurationToFormData(savedConfig));
  }, [savedConfig, editingSection]);

  const updateMutation = useMutation(configurationAPI.update, {
    onSuccess: (response) => {
      const saved = extractConfiguration(response);
      if (saved) {
        const next = configurationToFormData(saved);
        setFormData(next);
        saveInvoicePrintSettingsToLocalStorage(next.invoicePrintSettings);
        applyUiThemeToDocument(next.uiTheme);
        writeCachedUiTheme(tenantId, next.uiTheme);
        queryClient.setQueryData('configuration', response);
      } else {
        queryClient.invalidateQueries('configuration');
      }
      queryClient.invalidateQueries('dashboard-overview');
      queryClient.invalidateQueries('config-print');
      queryClient.invalidateQueries(['configuration-theme', tenantId]);
      setEditingSection(null);
      toast.success('Configuración actualizada exitosamente');
    },
    onError: (error: any) => {
      toast.error('Error al actualizar la configuración: ' + (error.response?.data?.message || error.message));
    }
  });

  const uploadLogoMutation = useMutation(configurationAPI.uploadLogo, {
    onSuccess: () => {
      queryClient.invalidateQueries('configuration');
      setLogoFile(null);
      setPreviewUrl(null);
      toast.success('Logo actualizado exitosamente');
    },
    onError: (error: any) => {
      toast.error('Error al actualizar el logo: ' + (error.response?.data?.message || error.message));
    }
  });

  const deleteLogoMutation = useMutation(configurationAPI.deleteLogo, {
    onSuccess: () => {
      queryClient.invalidateQueries('configuration');
      toast.success('Logo eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error('Error al eliminar el logo: ' + (error.response?.data?.message || error.message));
    }
  });

  const resetMutation = useMutation(configurationAPI.resetToDefaults, {
    onSuccess: (response) => {
      const saved = extractConfiguration(response);
      if (saved) {
        const next = configurationToFormData(saved);
        setFormData(next);
        saveInvoicePrintSettingsToLocalStorage(next.invoicePrintSettings);
        applyUiThemeToDocument(next.uiTheme);
        writeCachedUiTheme(tenantId, next.uiTheme);
        queryClient.setQueryData('configuration', response);
      } else {
        queryClient.invalidateQueries('configuration');
      }
      queryClient.invalidateQueries('dashboard-overview');
      queryClient.invalidateQueries('config-print');
      queryClient.invalidateQueries(['configuration-theme', tenantId]);
      setEditingSection(null);
      setShowResetModal(false);
      toast.success('Configuración restablecida a valores por defecto');
    },
    onError: (error: any) => {
      toast.error('Error al restablecer la configuración: ' + (error.response?.data?.message || error.message));
    }
  });

  const startEditSection = (section: ConfigSection) => {
    if (savedConfig) {
      setFormData(configurationToFormData(savedConfig));
      applyUiThemeToDocument(normalizeUiTheme(savedConfig.uiTheme));
    }
    setEditingSection(section);
  };

  const handleCancelSection = () => {
    if (savedConfig) {
      const next = configurationToFormData(savedConfig);
      setFormData(next);
      applyUiThemeToDocument(next.uiTheme);
    }
    setEditingSection(null);
  };

  const handleSaveSection = () => {
    updateMutation.mutate(formData);
  };

  const handleUiThemeChange = (uiTheme: UiTheme) => {
    const next = normalizeUiTheme(uiTheme);
    setFormData((p) => ({ ...p, uiTheme: next }));
    if (editingSection === 'appearance') {
      applyUiThemeToDocument(next);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUploadLogo = () => {
    if (logoFile) {
      const formData = new FormData();
      formData.append('logo', logoFile);
      uploadLogoMutation.mutate(formData);
    }
  };

  const handleDeleteLogo = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Logo',
      message: '¿Está seguro de que desea eliminar el logo actual? Esta acción no se puede deshacer.',
      type: 'warning',
      onConfirm: () => {
        deleteLogoMutation.mutate();
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleReset = () => {
    resetMutation.mutate();
  };

  const currencySelectOptions = useMemo(() => {
    const set = new Set(CURRENCY_OPTIONS.map((o) => o.value));
    if (!set.has(formData.currencyCode)) {
      return [
        { value: formData.currencyCode, label: `${formData.currencyCode} (actual)` },
        ...CURRENCY_OPTIONS
      ];
    }
    return CURRENCY_OPTIONS;
  }, [formData.currencyCode]);

  const localeSelectOptions = useMemo(() => {
    const set = new Set(LOCALE_OPTIONS.map((o) => o.value));
    if (!set.has(formData.locale)) {
      return [
        { value: formData.locale, label: `${formData.locale} (actual)` },
        ...LOCALE_OPTIONS
      ];
    }
    return LOCALE_OPTIONS;
  }, [formData.locale]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const config = savedConfig;

  // Solo administradores pueden acceder a la configuración
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
          <p className="text-gray-600">No tienes permisos para acceder a la configuración del sistema.</p>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute permission="configuration.read">
      <div className="space-y-8 animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configuración del Sistema</h1>
          <p className="mt-2 text-gray-600">
            Personaliza la información de la empresa y configuración general
          </p>
        </div>
      </div>

      <SubscriptionPlanCard />

      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center mb-0">
            <BuildingLibraryIcon className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Centro Fiscal DGII</h2>
              <p className="text-sm text-gray-600 mt-1">
                RNC, secuencias e-NCF, impuestos, certificado digital y facturación mensual
              </p>
            </div>
          </div>
          <Link
            to="/configuration/fiscal"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            Abrir centro fiscal
          </Link>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center mb-0">
            <ClipboardDocumentListIcon className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Auditoría e historial</h2>
              <p className="text-sm text-gray-600 mt-1">
                Quién hizo qué, cuándo y qué cambió en el sistema (solo administradores)
              </p>
            </div>
          </div>
          <Link
            to="/configuration/audit"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            Ver historial
          </Link>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center mb-0">
            <EnvelopeIcon className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Notificaciones por correo</h2>
              <p className="text-sm text-gray-600 mt-1">
                Resúmenes semanales, recordatorios y auditoría de envíos
              </p>
            </div>
          </div>
          {hasEmailAutomation ? (
            <Link
              to="/configuration/email-notifications"
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              Configurar correos
            </Link>
          ) : loadingEmailFeature ? (
            <LoadingSpinner size="sm" />
          ) : (
            <PlanFeatureUpgradePrompt feature="emailAutomation" variant="compact" />
          )}
        </div>
      </Card>

      {/* Logo Section */}
      <Card className="p-6">
        <div className="flex items-center mb-6">
          <PhotoIcon className="h-6 w-6 text-primary-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">Logo de la Empresa</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Current Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo Actual
            </label>
                {config?.logo ? (
              <div className="relative">
                <img
                  src={`${(import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3001')}${config.logo}`}
                  alt="Logo actual"
                  className="w-32 h-32 object-contain border border-gray-300 rounded-lg"
                />
                <Button
                  onClick={handleDeleteLogo}
                  variant="outline"
                  size="sm"
                  className="absolute -top-2 -right-2 bg-white"
                  loading={deleteLogoMutation.isLoading}
                >
                  <XMarkIcon className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <span className="text-gray-500 text-sm">Sin logo</span>
              </div>
            )}
          </div>

          {/* Upload New Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subir Nuevo Logo
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              aria-label="Seleccionar archivo de logo"
            />
            {previewUrl && (
              <div className="mt-4">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-32 h-32 object-contain border border-gray-300 rounded-lg"
                />
                <Button
                  onClick={handleUploadLogo}
                  variant="danger"
                  size="sm"
                  className="mt-2"
                  loading={uploadLogoMutation.isLoading}
                >
                  Subir Logo
                </Button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Formatos permitidos: JPEG, PNG, GIF, WebP. Tamaño máximo: 5MB
            </p>
          </div>
        </div>
      </Card>

      {/* Company Information */}
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center min-w-0">
            <BuildingOfficeIcon className="h-6 w-6 text-primary-600 mr-3 shrink-0" />
            <h2 className="text-xl font-semibold text-gray-900">Información de la Empresa</h2>
          </div>
          <SectionEditActions
            isEditing={editingSection === 'company'}
            saving={updateMutation.isLoading && editingSection === 'company'}
            onEdit={() => startEditSection('company')}
            onCancel={handleCancelSection}
            onSave={handleSaveSection}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Nombre de la Empresa *"
            name="companyName"
            value={formData.companyName}
            onChange={(v) => setFormData((p) => ({ ...p, companyName: v }))}
            disabled={editingSection !== 'company'}
            placeholder="Ej: FUNERARIA SANTA MARÍA"
          />

          <Input
            label="RUC *"
            name="ruc"
            value={formData.ruc}
            onChange={(v) => setFormData((p) => ({ ...p, ruc: v }))}
            disabled={editingSection !== 'company'}
            placeholder="Ej: 1234567890123"
          />

          <Input
            label="Dirección *"
            name="address"
            value={formData.address}
            onChange={(v) => setFormData((p) => ({ ...p, address: v }))}
            disabled={editingSection !== 'company'}
            placeholder="Ej: Av. Principal #123, Ciudad"
          />

          <Input
            label="Teléfono *"
            name="phone"
            value={formData.phone}
            onChange={(v) => setFormData((p) => ({ ...p, phone: v }))}
            disabled={editingSection !== 'company'}
            placeholder="Ej: (809) 123-4567"
          />

          <Input
            label="Email *"
            name="email"
            type="email"
            value={formData.email}
            onChange={(v) => setFormData((p) => ({ ...p, email: v }))}
            disabled={editingSection !== 'company'}
            placeholder="Ej: info@funerariasantamaria.com"
          />

          <Input
            label="Sitio Web"
            name="website"
            value={formData.website}
            onChange={(v) => setFormData((p) => ({ ...p, website: v }))}
            disabled={editingSection !== 'company'}
            placeholder="Ej: https://www.funerariasantamaria.com"
          />
        </div>
      </Card>

      {/* Currency and region */}
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center min-w-0">
            <GlobeAltIcon className="h-6 w-6 text-primary-600 mr-3 shrink-0" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Moneda y región</h2>
              <p className="text-sm text-gray-600 mt-1">
                Afecta el formato de montos y fechas en el panel y vistas que usen esta configuración.
              </p>
            </div>
          </div>
          <SectionEditActions
            isEditing={editingSection === 'regional'}
            saving={updateMutation.isLoading && editingSection === 'regional'}
            onEdit={() => startEditSection('regional')}
            onCancel={handleCancelSection}
            onSave={handleSaveSection}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="currencyCode" className="block text-sm font-bold text-gray-800 mb-2">
              Moneda (ISO 4217)
            </label>
            <select
              id="currencyCode"
              name="currencyCode"
              value={formData.currencyCode}
              onChange={(e) =>
                setFormData((p) => ({ ...p, currencyCode: e.target.value }))
              }
              disabled={editingSection !== 'regional'}
              className="block w-full border border-gray-300 rounded-xl shadow-sm px-4 py-3 bg-white text-gray-900 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {currencySelectOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="locale" className="block text-sm font-bold text-gray-800 mb-2">
              Idioma y región
            </label>
            <select
              id="locale"
              name="locale"
              value={formData.locale}
              onChange={(e) =>
                setFormData((p) => ({ ...p, locale: e.target.value }))
              }
              disabled={editingSection !== 'regional'}
              className="block w-full border border-gray-300 rounded-xl shadow-sm px-4 py-3 bg-white text-gray-900 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {localeSelectOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Facturación / fiscal — movido al Centro Fiscal DGII */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center min-w-0">
            <CalendarDaysIcon className="h-6 w-6 text-primary-600 mr-3 shrink-0" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Facturación automática</h2>
              <p className="text-sm text-gray-600 mt-1">
                La programación de cuotas mensuales, e-CF e impuestos se gestiona en el Centro Fiscal DGII.
                Los campos legacy de esta página quedan deprecados.
              </p>
            </div>
          </div>
          <Link
            to="/configuration/fiscal"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium shrink-0"
          >
            Configurar en Centro Fiscal
          </Link>
        </div>
      </Card>

      {/* Theme / appearance */}
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center min-w-0">
            <SwatchIcon className="h-6 w-6 text-primary-600 mr-3 shrink-0" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Apariencia</h2>
              <p className="text-sm text-gray-600 mt-1">
                Color de marca del panel para esta funeraria. Todos los usuarios del mismo negocio verán el mismo tema.
              </p>
            </div>
          </div>
          <SectionEditActions
            isEditing={editingSection === 'appearance'}
            saving={updateMutation.isLoading && editingSection === 'appearance'}
            onEdit={() => startEditSection('appearance')}
            onCancel={handleCancelSection}
            onSave={handleSaveSection}
          />
        </div>
        <div className="max-w-2xl">
          <UiThemeSettingsForm
            theme={formData.uiTheme}
            disabled={editingSection !== 'appearance'}
            onChange={handleUiThemeChange}
          />
        </div>
      </Card>

      {/* Invoice print settings */}
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center min-w-0">
            <DocumentTextIcon className="h-6 w-6 text-primary-600 mr-3 shrink-0" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Ajustes de factura</h2>
              <p className="text-sm text-gray-600 mt-1">
                Define el tamaño de impresión y cómo se muestra la factura al imprimir desde el sistema.
              </p>
            </div>
          </div>
          <SectionEditActions
            isEditing={editingSection === 'invoice'}
            saving={updateMutation.isLoading && editingSection === 'invoice'}
            onEdit={() => startEditSection('invoice')}
            onCancel={handleCancelSection}
            onSave={handleSaveSection}
          />
        </div>
        <div className="max-w-md">
          <InvoicePrintSettingsForm
            settings={formData.invoicePrintSettings}
            disabled={editingSection !== 'invoice'}
            onChange={(invoicePrintSettings) =>
              setFormData((p) => ({ ...p, invoicePrintSettings }))
            }
          />
        </div>
      </Card>

      {/* Reset Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Restablecer Configuración</h2>
            <p className="text-gray-600 mt-1 hidden sm:block">
              Restablece toda la configuración a los valores por defecto del sistema
            </p>
          </div>
          <Button
            onClick={() => setShowResetModal(true)}
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Restablecer
          </Button>
        </div>
      </Card>

      {/* Reset Confirmation Modal */}
      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Confirmar Restablecimiento"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Está seguro de que desea restablecer toda la configuración a los valores por defecto?
            Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              onClick={() => setShowResetModal(false)}
              variant="outline"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReset}
              variant="danger"
              className="bg-red-600 hover:bg-red-700"
              loading={resetMutation.isLoading}
            >
              Restablecer
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        isLoading={deleteLogoMutation.isLoading}
      />
      </div>
    </ProtectedRoute>
  );
};

export default Configuration;
